/*
 * Copyright (c) 2008-2019 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */
import store from "@/store"
import { VALUE_TYPES } from "@/utils/TypeCastHelpers"

/* global log */

/**
 * Indicates whether access is given based on the provided license feature.
 * Ensures that feature guards are ignored for non-tenant users without affecting the
 * license store.
 *
 * @param licensePermission
 * @returns {Boolean}
 */
const isFeatureLicensed = function (licenseFeature) {
  let user = store.getters["user/userSettings"]

  // getter ensures a check for parameter validity is not needed
  return (
    (!!user.tenantId &&
      store.getters["effectiveLicenses/getEffectiveLicenseFeature"](
        licenseFeature,
        null,
        VALUE_TYPES.BOOLEAN,
        user.tenantId
      )) ||
    !user.tenantId
  )
}

/**
 * Handles the logic to ensure the effective license has been loaded and can be used
 * to validate against. This includes first checking if the license is already loaded
 * and if not triggers the load of the effective license before resolving to allow the caller to
 * proceed if waiting.
 *
 * To put it differently: This function does the following: Once the currently logged-in user is loaded, load the
 * license of the associated tenant (if a tenant exists) and return the whole thing as a promise so that you can hook
 * into it (i.e., once the license has been loaded).
 *
 * @returns {Promise}
 */
const ensureLicenseLoaded = function () {
  return new Promise((resolve) => {
    // Already loaded by another request let the listener carry on immediately
    if (!store.getters["effectiveLicenses/isEffectiveLicenseNotLoaded"]) {
      resolve()
      return
    }

    log.debug("License information not loaded, wiring up watcher and callback logic.")
    let unwatchCallback = null
    const triggerLicenseLoadCallback = (function () {
      // Callback locking mechanism
      let hasTriggeredLicenseLoad = false
      return function (currentUser) {
        // If this callback has already handled
        if (hasTriggeredLicenseLoad) return

        // Lock the callback
        hasTriggeredLicenseLoad = true

        // This call back should never be null, however on the off chance is, verify before calling
        if (unwatchCallback) unwatchCallback()

        let tenantId = currentUser.tenantId
        log.debug(`Triggering load of effective license for ${tenantId}`)
        // WARNING: you may think it's a brilliant idea to load the tenantFilterId here to make sure it's set.
        // It's really not. The tenantFilterId is misused to store the logged in tenantId in case of tenant-users.
        // I.e. the tenantFilterId loaded in App.vue is overwritten by user.js when the current user is loaded.
        // DO NOT overwrite this value here or you will see tenant select boxes where none should be and other weird
        // behavior.
        // store.commit("tenants/setTenantFilterId", { loadFromWebStorage: true })
        const selectedTenantId = store.getters["tenants/getTenantFilterId"]

        const tenantsToLoadLicensesFor =
          selectedTenantId === tenantId || !selectedTenantId ? [tenantId] : [tenantId, selectedTenantId]
        // Now load license permissions,
        store.dispatch("effectiveLicenses/loadEffectiveLicenses", tenantsToLoadLicensesFor).then(
          () => {
            log.debug("License information loaded.")
            // Remove the store watch, so this callback is not accidentally fired again
            resolve()
          },
          (error) => {
            log.error(
              `Failed to load licensing information for ${tenantId} / ${tenantsToLoadLicensesFor}: `,
              JSON.stringify(error)
            )
            resolve()
          }
        )
      }
    })()

    // Add a watch to the current user's information in state, thus if the
    // proceeding logic is unable to get a valid user, the callback is triggered
    // when the user's information is finally loaded in.
    unwatchCallback = store.watch(() => store.getters["user/userSettings"], triggerLicenseLoadCallback)

    let currentUser = store.getters["user/userSettings"]

    // Check for a valid user, if present fire the callback now and let the callback
    // handle locking and triggering the unwatch
    if (currentUser?.id) triggerLicenseLoadCallback(currentUser)
  })
}

export { isFeatureLicensed, ensureLicenseLoaded }
